"use client";

import { useState, useEffect, useRef } from "react";
import { useUser } from "@clerk/nextjs";
import { usePathname } from "next/navigation";
import ProNagModal from "@/components/ProNagModal";

const SCROLL_TITLES = [
  "Endlessly scrolling to find what you want?",
  "Still hunting for the perfect piece?",
  "Save your search, stop the scroll.",
  "ACCELERATE YOUR SEARCH",
];

const MIN_SCROLLS = 4;
const MAX_SCROLLS = 7;

function randomThreshold() {
  return Math.floor(Math.random() * (MAX_SCROLLS - MIN_SCROLLS + 1)) + MIN_SCROLLS;
}

export default function ScrollNagManager() {
  const { isSignedIn } = useUser();
  const pathname = usePathname();
  const [showNag, setShowNag] = useState(false);
  const scrollCount = useRef(0);
  const threshold = useRef(randomThreshold());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isSignedIn || pathname !== "/search") return;

    const handleScroll = (e: Event) => {
      const target = e.target as Element;
      if (target?.closest?.('[role="dialog"], [data-scroll-nag-exclude]')) return;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        scrollCount.current += 1;
        if (scrollCount.current >= threshold.current) {
          scrollCount.current = 0;
          threshold.current = randomThreshold();
          setShowNag(true);
        }
      }, 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [isSignedIn, pathname]);

  if (isSignedIn || pathname !== "/search") return null;

  return (
    <ProNagModal
      isOpen={showNag}
      onClose={() => setShowNag(false)}
      source="scroll_nag"
      titles={SCROLL_TITLES}
      subtitle="Sign up to unlock daily tokens, saved filters, and price drop alerts."
    />
  );
}
